import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Send, Loader2, Trash2, BookOpen } from 'lucide-react'
import { api } from '@/lib/api'
import { HELP_ARTICLES } from '@/data/helpArticles'
import { renderSofiaMarkdown } from '@/lib/sofiaMarkdown'

interface Msg { role: 'user' | 'assistant'; content: string; artigos?: { id: string; titulo: string }[] }

const norm = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

// Busca local nos artigos de ajuda — mantém a Sofia útil mesmo sem a IA configurada.
function buscarAjudaLocal(pergunta: string): { content: string; artigos: { id: string; titulo: string }[] } {
  const termos = norm(pergunta).split(/\s+/).filter(t => t.length > 2)
  const ranqueados = HELP_ARTICLES.map(a => {
    const t = norm(a.titulo), tags = norm(a.tags.join(' ')), r = norm(a.resumo), c = norm(a.conteudo)
    let score = 0
    for (const termo of termos) {
      if (t.includes(termo)) score += 5
      if (tags.includes(termo)) score += 4
      if (r.includes(termo)) score += 2
      if (c.includes(termo)) score += 1
    }
    return { a, score }
  }).filter(x => x.score > 0).sort((x, y) => y.score - x.score).slice(0, 3)

  if (ranqueados.length === 0) {
    return {
      content: 'Não achei um guia específico pra isso. Tente palavras como "PDV", "pedido", "faturar", "imposto", "entregador" ou "estoque" — ou abra a Central de Ajuda pra ver todos os tópicos.',
      artigos: [],
    }
  }
  const top = ranqueados[0].a
  return {
    content: `Encontrei estes guias que devem te ajudar. O mais próximo é "${top.titulo}" — ${top.resumo} Toque pra abrir o passo a passo:`,
    artigos: ranqueados.map(x => ({ id: x.a.id, titulo: x.a.titulo })),
  }
}

export default function AiChat({ onClose }: { onClose: () => void }) {
  const [msgs, setMsgs] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [msgs])

  function abrirArtigo(id: string) {
    onClose()
    navigate(`/ajuda?art=${id}`)
  }

  async function enviarTexto(texto: string) {
    const pergunta = texto.trim()
    if (!pergunta || loading) return
    setInput('')
    const novas: Msg[] = [...msgs, { role: 'user', content: pergunta }]
    setMsgs(novas)
    setLoading(true)
    try {
      const { data } = await api.post('/ia/chat', {
        mensagem: pergunta,
        historico: novas.slice(-10),
      })
      setMsgs(prev => [...prev, { role: 'assistant', content: data.resposta }])
    } catch {
      // Sem IA (ou falha): responde a partir dos artigos de ajuda — Sofia nunca fica "muda".
      const local = buscarAjudaLocal(pergunta)
      setMsgs(prev => [...prev, { role: 'assistant', content: local.content, artigos: local.artigos }])
    } finally {
      setLoading(false)
    }
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviarTexto(input) }
  }

  const sugestoes = [
    'Como vender no balcão (PDV)?',
    'Como faturar um pedido?',
    'Como funciona o imposto pro Piauí?',
    'Como o entregador usa o app?',
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-ink-200 shrink-0">
        <div className="relative shrink-0">
          <img src="/sofia.jpg" alt="Sofia" className="h-9 w-9 rounded-full object-cover ring-2 ring-brand-100" />
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-900">Sofia</div>
          <div className="text-[11px] text-ink-500">Sua ajudante — pergunte em linguagem simples</div>
        </div>
        {msgs.length > 0 && (
          <button onClick={() => setMsgs([])} title="Limpar" className="p-1.5 rounded hover:bg-ink-100 text-ink-400">
            <Trash2 size={14} />
          </button>
        )}
        <button onClick={onClose} className="p-1.5 rounded hover:bg-ink-100 text-ink-500"><X size={16} /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.length === 0 && (
          <div className="text-center py-8">
            <img src="/sofia.jpg" alt="Sofia" className="h-16 w-16 rounded-full object-cover mx-auto mb-3 ring-2 ring-brand-100" />
            <p className="text-sm text-ink-500 mb-4">Oi! Sou a Sofia. Como posso ajudar?</p>
            <div className="space-y-2">
              {sugestoes.map((sug) => (
                <button key={sug} onClick={() => enviarTexto(sug)}
                  className="block w-full text-left text-xs px-3 py-2 rounded-md bg-ink-50 hover:bg-ink-100 text-ink-700 transition-colors">
                  {sug}
                </button>
              ))}
            </div>
          </div>
        )}

        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={m.role === 'user' ? 'max-w-[85%]' : 'w-full'}>
              {m.role === 'user' ? (
                <div className="rounded-lg px-3 py-2 text-sm whitespace-pre-wrap bg-brand-600 text-white rounded-br-sm">
                  {m.content}
                </div>
              ) : (
                <div className="sofia-md rounded-lg px-3 py-2 text-sm bg-ink-100 text-ink-900 rounded-bl-sm"
                  dangerouslySetInnerHTML={{ __html: renderSofiaMarkdown(m.content) }} />
              )}
              {m.artigos && m.artigos.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {m.artigos.map(art => (
                    <button key={art.id} onClick={() => abrirArtigo(art.id)}
                      className="flex items-center gap-2 w-full text-left text-xs px-3 py-2 rounded-md bg-violet-50 hover:bg-violet-100 text-violet-800 font-medium transition-colors">
                      <BookOpen size={14} className="shrink-0" /> {art.titulo}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-ink-100 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-ink-500">
              <Loader2 size={14} className="animate-spin" /> Pensando...
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-ink-200 shrink-0">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Pergunte algo..."
            rows={1}
            className="flex-1 resize-none input !py-2 text-sm"
            disabled={loading}
          />
          <button onClick={() => enviarTexto(input)} disabled={!input.trim() || loading}
            className="btn-primary !px-3 !py-2 shrink-0">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
